import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { Button, OutlinedInput, Typography } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  sxFormError,
  sxLabel,
} from "../../../assets/styles/CommonSxProps";
import LoadingApp from "../../../components/LoadingApp";
import ModalAction from "../../../components/ModalAction";
import ModalFeedBack from "../../../components/ModalFeedBack";
import { ExcluirContaValidation } from "../../../interfaces/validation/ExcluirConta/ExcluirContaValidation";
import { cancelarRegistroBeneficiario } from "../../../services/LoginService";
import { cpfMask } from "../../../utils/CpfMask";
import { sxTypographyAdvice } from "../../SaudeOnline/MeusAgendamentos/sxProps";
import "./styles.css";
import { sxButtonExcluirConta, sxInputCpf } from "./sxProps";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import PageTitle from "../../../components/Accessibility/PageTitle";

const ExcluirConta: React.FC = () => {
  const accessibility = useAccessibility();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: "error" | "success" | "warning";
    title?: string;
    textButton?: string;
    subTitle?: string;
    closeAction: () => void;
    showButtonModal: boolean;
    buttonAction?: () => void;
  }>(null);
  const [modalAction, setModalAction] = useState<null | {
    handleShowModal?: () => void;
    textOutlinedButton: string;
    textContainedButton: string;
    title: string;
    subTitle?: string;
    handleOutlinedButton?: () => void;
    handleContainedButton?: () => void;
  }>(null);

  const excluirConta = async (cpf: string) => {
    handleCloseModalAction();
    setLoading(true);

    const response = await cancelarRegistroBeneficiario();
    if (response?.status === 200) {
      openModalSuccess(
        "Sua conta foi excluída",
        "Para criar uma nova conta, acesse o Canal do Cliente ou Aplicativo Unimed Campinas."
      );
    } else {
      console.error(response);
      openModalError(
        "Erro ao consultar simulação",
        "Ocorreu um erro ao consultar sua simulação. Tente novamente em instantes."
      );
    }

    setLoading(false);
  };

  const openModalConfirmDeleteAccount = () => {
    setModalAction({
      title: "Excluir conta",
      subTitle:
        "Tem certeza que deseja excluir sua conta? A exclusão da conta no Canal do Cliente e Aplicativo Unimed Campinas não tem efeitos para o Cancelamento do Plano.",
      textOutlinedButton: "CANCELAR",
      textContainedButton: "EXCLUIR CONTA",
      handleContainedButton: () => excluirConta(formik.values.cpf),
      handleOutlinedButton: handleCloseModalAction,
      handleShowModal: handleCloseModalAction,
    });
  };

  const handleCloseModalAction = () => {
    setModalAction(null);
  };

  const formik = useFormik({
    initialValues: {
      cpf: "",
    },
    validationSchema: ExcluirContaValidation,
    onSubmit: () => {
      openModalConfirmDeleteAccount();
    },
    validateOnChange: false,
  });

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
    mask: (inputValue: string) => string | null,
    formik: FormikProps<any>
  ) => {
    const { value } = event.target as HTMLInputElement;
    formik.setFieldValue(field, mask ? mask(value) : value);
  };

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: "error",
      title: title,
      subTitle: subtitle,
      textButton: "FECHAR",
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null),
    });
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/login");
  };

  const openModalSuccess = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: "success",
      title: title,
      subTitle: subtitle,
      textButton: "OK, ENTENDI",
      showButtonModal: true,
      closeAction: handleLogout,
      buttonAction: handleLogout,
    });
  };

  return (
    <>
      <LoadingApp showLoading={loading} />
      <div className="excluir-conta-container">
      <PageTitle text="Excluir conta" />
        <div
          className="excluir-conta-advice-container"
          style={{
            ...accessibility.contrastBorderAccessibility,
            ...accessibility.contrastStyle,
          }}
        >
          <Typography
            style={accessibility.textStyleAccessibilityWithMargin}
            sx={sxTypographyAdvice}
          >
            Ao excluir sua conta, você perderá acesso no <b>Canal do Cliente</b>{" "}
            e <b>Aplicativo Unimed Campinas</b>.
          </Typography>
          <Typography
            style={accessibility.textStyleAccessibilityWithMargin}
            sx={sxTypographyAdvice}
          >
            A exclusão da conta implicará na restrição de uso para todos os
            serviços digitais da Unimed Campinas. Além disso, caso queira
            acessar os serviços disponíveis nesses canais no futuro, deverá
            criar uma nova conta.
          </Typography>
          <Typography
            style={accessibility.textStyleAccessibility}
            sx={sxTypographyAdvice}
          >
            Para continuar, por favor, confirme seus dados.
          </Typography>
        </div>
        <form
          className="excluir-conta-form-container"
          onSubmit={formik.handleSubmit}
        >
          <div>
            <Typography
              style={accessibility.textStyleAccessibility}
              sx={sxLabel}
            >
              CPF
            </Typography>
            <label aria-label="cpf" htmlFor="cpf" />
            <OutlinedInput
              id="cpf"
              style={accessibility.textStyleAccessibility}
              type={"text"}
              inputMode="numeric"
              value={formik.values.cpf}
              onChange={(e) => handleInputChange(e, "cpf", cpfMask, formik)}
              name="cpf"
              error={!!formik.errors.cpf}
              placeholder='999.999.999-99'
              maxRows={1}
              inputProps={{ maxLength: 14 }}
              sx={sxInputCpf}
            />
            {formik.errors.cpf && (
              <Typography tabIndex={0} sx={sxFormError}>
                {formik.errors.cpf}
              </Typography>
            )}
          </div>
          <div className="buttons-container">
            <Button
              variant="contained"
              sx={sxButtonExcluirConta}
              style={accessibility.buttonStyleAccessibility}
              disabled={!formik.values.cpf}
              type="submit"
            >
              <Typography style={accessibility.buttonTextStyleAccessibility}>
                EXCLUIR CONTA
              </Typography>
            </Button>
          </div>
        </form>
      </div>
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as "success" | "error" | "warning"}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
      <ModalAction
        showModal={!!modalAction}
        handleShowModal={modalAction?.handleShowModal}
        textOutlinedButton={modalAction?.textOutlinedButton || ""}
        textContainedButton={modalAction?.textContainedButton || ""}
        title={modalAction?.title || ""}
        icon="warning"
        subTitle={modalAction?.subTitle || ""}
        handleOutlinedButton={modalAction?.handleOutlinedButton}
        handleContainedButton={modalAction?.handleContainedButton}
      />
    </>
  );
};

export default ExcluirConta;
